import * as React from "react";
import { useEffect, useRef, useState } from "react";

import './geo-map.scss';

import { unByKey } from "ol/Observable";
import { Coordinate } from "ol/coordinate";
import { Tile as TileLayer, VectorTile as VectorTileLayer, Vector as VectorLayer } from "ol/layer";
import { fromLonLat } from "ol/proj";
import { OSM, VectorTile as VectorTileSource, Vector as VectorSource } from "ol/source";
import { createXYZ } from "ol/tilegrid";

import OlMap from "ol/Map";
import OlView from "ol/View";
import MVT from "ol/format/MVT";
import GeoJSON from 'ol/format/GeoJSON';
import Style from "ol/style/Style";

import { Legend, LegendConfig, NEUTRAL_STYLE, numberWithCommas } from "./legend";
import { FeatureLike } from "ol/Feature";

interface GeoMapProps {
	mapDivId?: string;
	onSelect?: (payload: any) => void;
	center?: Coordinate;
	styleFunction?: (propertyValue: string | number) => Style | Style[] | undefined;
	centroidStyleFunction?: (propertyValue: string | number) => Style | Style[] | undefined;
	geomConfig: GeomConfig | undefined,
	legendConfig: LegendConfig | undefined,
}

interface GeomConfig {
	polygons: string,
	centroids: string | undefined,
	poly_code_col: string,
	centroid_code_col: string
}

const defaultProps = {
	mapDivId: "geoMap",
	onSelect: undefined as any,
	center: [24.85, 46],
	styleFunction: undefined,
	centroidStyleFunction: undefined,
	geomConfig: {
		polygons: "geospatial:ro_judete_poligon",
		centroids: "recensamant:judete_centroid",
		poly_code_col: 'JUDET',
		centroid_code_col: 'cod'
	},
	legendConfig: undefined
};


const mapBaseUrl = "https://services.geo-spatial.org/geoserver/";



export const GeoMap: React.FC<GeoMapProps> = (props: GeoMapProps) => {
	const { mapDivId, onSelect, center, styleFunction, centroidStyleFunction, geomConfig, legendConfig } = props;

	const [listener, setListener] = useState<any>(null);

	const olMap = useRef<OlMap|null>(null);

	const olLegend = useRef<Legend | null>(null);

	useEffect(() => {
		olMap.current = new OlMap({
			target: undefined,
			view: new OlView({
				projection: "EPSG:3857",
				minZoom: 5,
				maxZoom: 12,
				zoom: window.screen.width <= 800 ? 6 : 7,
				center: fromLonLat(center || defaultProps.center, "EPSG:3857"),
			}),
			layers: [
				new TileLayer({
					source: new OSM(),
					visible: true,
				}),
				new VectorTileLayer({
					source: undefined,
					visible: false
				}),
				new VectorLayer({
					source: undefined,
					visible: false,
					style: null
				})
			],
		});
		olLegend.current = new Legend({
			margin: 5,
			collapsed: false,
			size: [13, 13],
			fontSize: 12,
		});
		olMap.current.addControl(olLegend.current);
		olLegend.current.setInitialVisibility(true);

		return () => {
			olMap.current?.setTarget(undefined);
			if (olLegend.current)
				olMap.current?.removeControl(olLegend.current);
			olLegend.current?.clear();
			if (listener) unByKey(listener);
		};
	}, []);

	useEffect(() => {
		if (olMap) {
			if (geomConfig?.polygons) {
				const vectorSource = new VectorTileSource({
					tileGrid: createXYZ({ maxZoom: 12 }),
					format: new MVT(),
					url: `${mapBaseUrl}gwc/service/tms/1.0.0/${geomConfig?.polygons}@EPSG%3A3857@pbf/{z}/{x}/{-y}.pbf`,
				});
				(
					olMap.current?.getLayers().item(1) as VectorTileLayer
				).setSource(vectorSource);
				olMap.current?.getLayers().item(1).setVisible(true);
			} else olMap.current?.getLayers().item(1).setVisible(false);

			if (geomConfig?.centroids) {
				const vectorSource = new VectorSource({
					format: new GeoJSON(),
					url: `${mapBaseUrl}recensamant/ows?service=WFS&version=1.0.0&request=GetFeature&typeName=${geomConfig?.centroids}` +
						'&maxFeatures=5000&outputFormat=application/json&srsname=EPSG:3857'
				});
				(
					olMap.current?.getLayers().item(2) as VectorLayer<VectorSource>
				).setSource(vectorSource);
				olMap.current?.getLayers().item(2).setVisible(true);
			} else olMap.current?.getLayers().item(2).setVisible(false);
			olMap.current?.updateSize();
		}
	}, [geomConfig]);

	useEffect(() => {
		if (olMap) {
			olMap.current?.setTarget(mapDivId);
			olMap.current?.updateSize();
		}
	}, [mapDivId]);

	useEffect(() => {
		if (olMap && center) {
			olMap.current?.getView().setCenter(fromLonLat(center));
		}
	}, [center]);

	useEffect(() => {
		if (olMap && center) {
			(olMap.current?.getLayers().item(1) as VectorTileLayer).setStyle((feat: FeatureLike, res: number) => {
				if (styleFunction && geomConfig?.poly_code_col)
					return styleFunction(feat.get(geomConfig.poly_code_col));
			})
		}
	}, [styleFunction]);

	useEffect(() => {
		if (olMap && center) {
			if (centroidStyleFunction) {
				(olMap.current?.getLayers().item(2) as VectorLayer<VectorSource>).setStyle((feat: FeatureLike, res: number) => {
					return centroidStyleFunction(feat.get("cod"));
				})
				olMap.current?.getLayers().item(2).setVisible(true);
			}
			else
				olMap.current?.getLayers().item(2).setVisible(false);
		}
	}, [centroidStyleFunction]);

	useEffect(() => {
		if (olMap && center && legendConfig && olLegend?.current) {
			olLegend.current.clear();
			olLegend.current.setGradient(legendConfig.colors, legendConfig.thresholds);
			olLegend.current.addRow({ typeGeom: 'Polygon', style: NEUTRAL_STYLE, title: "Valoare confidențială" });
			if (!legendConfig.hasPie)
				olLegend.current.setPie(false);
			else {
				olLegend.current.setPie(true, legendConfig.pieSizes[0], legendConfig.pieSizes[1],
					numberWithCommas(legendConfig.pieLimits[0]), numberWithCommas(legendConfig.pieLimits[1]));
				console.log(legendConfig.pieRows);
				for (var pieRow of legendConfig.pieRows)
					olLegend.current.addPieRow(pieRow);
			}
			olLegend.current.refresh();
			olLegend.current.show();
		}
		else if(olLegend?.current) {
			olLegend.current.hide();
		}
	}, [legendConfig]);

	useEffect(() => {
		if (olMap && onSelect) {
			if (listener) unByKey(listener);
			setListener(
				(olMap.current as OlMap).on("click", (evt) => {
					(olMap.current?.getLayers().item(1) as VectorTileLayer)
						.getFeatures(evt.pixel)
						.then((feats) => {
							if (feats && feats.length > 0) {
								onSelect(feats[0].getProperties());
							}
							else {
								onSelect(null);
							}
						})
				})
			);
		}
	}, [onSelect, geomConfig]);

	return <div className="GeoMap"><div className="map" id={mapDivId} /></div>;
};

GeoMap.defaultProps = defaultProps;
